import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { getAssignmentJobHistories } from "../../services/assignmentService";
import ConversationProcessingJobsDetailsModal from "./ConversationProcessingJobsDetailsModal";
import {
  FaExclamation,
  FaCheck,
  FaHourglassHalf,
  FaSpinner,
  FaChevronDown,
} from "react-icons/fa";

const ConversationProcessingJobsTabView = ({ session }) => {
  const [assignmentJobs, setAssignmentJobs] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const JOBS_PER_PAGE = 40;

  useEffect(() => {
    if (!session?.conversation_processing_job_ids) {
      setAssignmentJobs({});
      setLoading(false);
      return;
    }

    setError(null);
    setCurrentPage(1);
    fetchJobs(1, true);
  }, [session?.conversation_processing_job_ids]);

  const fetchJobs = async (page, isInitialLoad = false) => {
    if (
      !session?.conversation_processing_job_ids ||
      session.conversation_processing_job_ids.length === 0
    ) {
      return;
    }

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const job_histories = await getAssignmentJobHistories(
        session?.assignment_id,
        page,
        JOBS_PER_PAGE,
      );

      if (job_histories.length === 0) {
        setHasMore(false);
        if (isInitialLoad) setLoading(false);
        else setLoadingMore(false);
        return;
      }
      const newJobIds = new Set();

      const newJobsMap = job_histories.reduce((acc, currentItem) => {
        const { job_run_id, ...rest } = currentItem;
        acc[job_run_id] = rest;
        return acc;
      }, {});

      if (isInitialLoad) {
        setAssignmentJobs(newJobsMap);
      } else {
        setAssignmentJobs((prevJobs) => ({
          ...prevJobs,
          ...newJobsMap,
        }));
      }

      setHasMore(JOBS_PER_PAGE * page >= job_histories.total);

      if (isInitialLoad) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    } catch (err) {
      console.error("Error fetching assignment:", err);
      setError("Failed to load assignment data");
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreJobs = async () => {
    const nextPage = currentPage + 1;
    await fetchJobs(nextPage);
    setCurrentPage(nextPage);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "FATAL_ERROR":
        return (
          <div className="flex items-center">
            <span className="mr-2 text-red-500 text-sm font-medium">Error</span>
            <FaExclamation className="text-red-500" title="Error" />
          </div>
        );
      case "NON_FATAL_ERROR":
        return (
          <div className="flex items-center">
            <span className="mr-2 text-red-500 text-sm font-medium">Error</span>
            <FaExclamation className="text-red-500" title="Error" />
          </div>
        );
      case "COMPLETED":
        return (
          <div className="flex items-center">
            <span className="mr-2 text-green-500 text-sm font-medium">
              Completed
            </span>
            <FaCheck className="text-green-500" title="Completed" />
          </div>
        );
      case "IN_PROGRESS":
        return (
          <div className="flex items-center">
            <span className="mr-2 text-yellow-500 text-sm font-medium">
              In Progress
            </span>
            <FaSpinner
              className="text-yellow-500 animate-spin"
              title="In Progress"
            />
          </div>
        );
      case "STARTED":
        return (
          <div className="flex items-center">
            <span className="mr-2 text-yellow-500 text-sm font-medium">
              Started
            </span>
            <FaHourglassHalf className="text-yellow-500" title="Started" />
          </div>
        );
      case "SUBMITTED":
        return (
          <div className="flex items-center">
            <span className="mr-2 text-yellow-500 text-sm font-medium">
              Submitted
            </span>
            <FaHourglassHalf className="text-yellow-500" title="Submitted" />
          </div>
        );
      case "CANCELLED":
        return (
          <div className="flex items-center">
            <span className="mr-2 text-gray-500 text-sm font-medium">
              Cancelled
            </span>
            <FaExclamation className="text-gray-500" title="Cancelled" />
          </div>
        );
      default:
        return (
          <div className="flex items-center">
            <span className="mr-2 text-gray-500 text-sm font-medium">
              Unknown
            </span>
            <FaHourglassHalf className="text-gray-500" title="Unknown Status" />
          </div>
        );
    }
  };

  function formatJobName(str) {
    if (!str) return "Unnamed Job";
    const words = str.split("_");
    const capitalizedWords = words.map((word) => {
      if (word.length === 0) return "";
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
    return capitalizedWords.join(" ");
  }

  const openJobModal = (jobId) => {
    setSelectedJob(jobId);
    setIsModalOpen(true);
  };

  const closeJobModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
  };

  if (!session?.conversation_processing_job_ids) {
    return (
      <p className="text-gray-400 p-4">
        No conversation processing jobs available
      </p>
    );
  }

  if (loading) {
    return <p className="text-gray-400 p-4">Loading job data...</p>;
  }

  if (error) {
    return <p className="text-red-400 p-4">{error}</p>;
  }

  if (!assignmentJobs || Object.keys(assignmentJobs).length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <p>There are no conversation processing jobs for this session.</p>
      </div>
    );
  }

  return (
    <div className="py-2 px-6 h-full overflow-y-auto dark-slim-scrollbar w-full">
      <div className="flex flex-col space-y-4">
        {Object.entries(assignmentJobs).map(([jobId, jobData]) => {
          const jobName = jobData.job_name || "Unnamed Job";
          const latestStatus = jobData.run_history[0]?.status;
          const comm_response = !!(
            jobData.run_history[0]?.result?.comm_service_responses ??
            jobData.run_history[0]?.result?.comm_service_response
          );

          const statusColorClass =
            latestStatus === "FATAL_ERROR" || latestStatus === "NON_FATAL_ERROR"
              ? "bg-red-500"
              : latestStatus === "COMPLETED" && comm_response
                ? "bg-green-500"
                : latestStatus === "COMPLETED" && !comm_response
                  ? "bg-gray-500"
                  : latestStatus === "CANCELLED"
                    ? "bg-gray-500"
                    : "bg-yellow-500";

          return (
            <div
              key={jobId}
              className="card-glass p-0 flex flex-row w-full cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => openJobModal(jobId)}
            >
              <div className={`w-2 rounded-l ${statusColorClass}`}></div>
              <div className="flex flex-col space-y-2 p-4 w-full">
                <div className="flex justify-between items-center">
                  <h3 className="text-gray-200 font-medium">{jobName}</h3>
                  <div
                    className="text-xl"
                    title={latestStatus.replace("_", " ")}
                  >
                    {getStatusIcon(latestStatus)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {hasMore && (
          <div className="flex justify-center pt-4 pb-8">
            <button
              onClick={loadMoreJobs}
              disabled={loadingMore}
              className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-800 disabled:opacity-70"
            >
              {loadingMore ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                <>
                  <FaChevronDown className="mr-2" />
                  Show more jobs
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {isModalOpen && selectedJob && (
        <ConversationProcessingJobsDetailsModal
          jobData={assignmentJobs[selectedJob]}
          onClose={closeJobModal}
          jobId={selectedJob}
        />
      )}
    </div>
  );
};

export default ConversationProcessingJobsTabView;
